/*
 * content-tabs.js — Content Tabs parent block driver.
 *
 * Architecture (mirrors product-tabs.js):
 *   The `<content-tabs-component>` host server-renders flat
 *   sibling pairs (button + panel per `_content-tab` child via
 *   content_for 'blocks'). This script reorganises that flat
 *   output at init into the conventional tabs DOM:
 *
 *     <content-tabs-component>
 *       <div class="content-tabs__header">[buttons]</div>
 *       <div class="content-tabs__content">[panels]</div>
 *     </content-tabs-component>
 *
 *   The first tab gets `.is-active` on init. Click + hover
 *   handlers swap the active button + panel; shopify:block:
 *   select activates the tab matching the editor selection.
 *
 * Fade orchestration:
 *   The CSS uses `display:none` on non-active panels (so the
 *   container respects the active panel's height rather than
 *   stretching to max-content). A direct display:none / display:
 *   block swap can't transition opacity, so a tab change runs
 *   in three frames:
 *     1. Current panel: opacity 1 -> 0 (transition duration)
 *     2. Current panel display:none + new panel display:block
 *        with opacity 0
 *     3. requestAnimationFrame, new panel opacity 0 -> 1
 *   The container's height snaps when display swaps, but the
 *   user perceives the swap as a fade -- both legs ride
 *   --global-transition-duration so the cadence matches the
 *   rest of the theme.
 *
 * Hover trigger:
 *   When the merchant picks `data-trigger="hover"`, mouseenter
 *   on a tab button schedules activation after HOVER_DWELL_MS.
 *   mouseleave clears the pending timer. This stops cursor
 *   fly-throughs from flashing panels.
 */

const SELECTOR = 'content-tabs-component';
const INSTANCE_KEY = '_contentTabsInstance';
const HOVER_DWELL_MS = 40;

class ContentTabs {
  constructor(root) {
    this.root = root;
    this.trigger = root.dataset.trigger === 'hover' ? 'hover' : 'click';
    this.abortController = new AbortController();
    this.buttons = [];
    this.panels = [];
    this.headerEl = null;
    this.contentEl = null;
    this._activeBtn = null;
    this._hoverTimer = null;
    this._fadeTimer = null;

    this.init();
  }

  init() {
    // Fast path: the inline pre-init script (in the Liquid file,
    // right after the host markup) has already rearranged the
    // DOM, set ARIA, activated the first tab, and stamped
    // `data-pre-init="done"` on the host. We only need to hook
    // up element references and bind interaction handlers.
    if (this.root.dataset.preInit === 'done') {
      this._initFromPreInit();
      return;
    }

    // Fallback path: no inline pre-init ran (defensive -- the
    // inline script could have been stripped by a minifier or
    // the host wasn't classed .not-loaded for some reason). Do
    // the full rearrangement in JS, same as the original flow.
    const flatButtons = Array.from(this.root.querySelectorAll(':scope > .content-tabs__tab-btn'));
    const flatPanels = Array.from(this.root.querySelectorAll(':scope > .content-tabs__tab-panel'));
    if (flatButtons.length === 0) return;

    const heading = this.root.querySelector(':scope > .content-tabs__heading');

    this.headerEl = document.createElement('div');
    this.headerEl.className = 'content-tabs__header';

    const tablistEl = document.createElement('div');
    tablistEl.className = 'content-tabs__tablist';
    tablistEl.setAttribute('role', 'tablist');

    this.contentEl = document.createElement('div');
    this.contentEl.className = 'content-tabs__content';

    flatButtons.forEach((btn, idx) => {
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-selected', 'false');
      btn.setAttribute('tabindex', '-1');
      const key = btn.getAttribute('data-tab');
      if (!btn.id) btn.id = `${key}-btn`;
      const panel = flatPanels.find((p) => p.getAttribute('data-tab') === key);
      if (panel) {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', btn.id);
        if (idx > 0) panel.classList.add('is-hidden');
        this.panels.push(panel);
      }
      this.buttons.push(btn);
      tablistEl.appendChild(btn);
    });

    this.panels.forEach((panel) => this.contentEl.appendChild(panel));

    if (heading) this.headerEl.appendChild(heading);
    this.headerEl.appendChild(tablistEl);
    this.root.replaceChildren(this.headerEl, this.contentEl);
    this.root.classList.remove('not-loaded');
    this.root.classList.add('is-initialized');

    this._bindClicks();
    this._bindKeyboard();
    this._bindHover();
    this._bindCustomizerSelect();

    if (this.buttons.length > 0) this._activateInstant(this.buttons[0]);
  }

  /**
   * Hook up references + bind interactions when the inline
   * pre-init script has already done the DOM rearrangement.
   * Reads the existing header / content / button / panel
   * elements + the first-active button straight from the DOM
   * the inline script produced. No DOM rewriting here -- the
   * layout the user is already looking at stays exactly as is.
   */
  _initFromPreInit() {
    this.headerEl = this.root.querySelector(':scope > .content-tabs__header');
    this.contentEl = this.root.querySelector(':scope > .content-tabs__content');
    if (!this.headerEl || !this.contentEl) return;

    this.buttons = Array.from(this.headerEl.querySelectorAll('.content-tabs__tab-btn'));
    this.panels = Array.from(this.contentEl.querySelectorAll('.content-tabs__tab-panel'));
    if (this.buttons.length === 0) return;

    this._activeBtn =
      this.buttons.find((b) => b.classList.contains('is-active')) || this.buttons[0];

    this._bindClicks();
    this._bindKeyboard();
    this._bindHover();
    this._bindCustomizerSelect();
  }

  _bindClicks() {
    this.buttons.forEach((btn) => {
      btn.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          this._activateWithFade(btn);
        },
        { signal: this.abortController.signal }
      );
    });
  }

  _bindHover() {
    if (this.trigger !== 'hover') return;
    // Hover trigger is desktop-only by intent.
    //
    // Init-time gate: skip listener binding entirely on touch-
    // primary devices (phones, tablets without a mouse) where
    // `(hover: hover) and (pointer: fine)` is false. The click
    // handler (always bound, irrespective of trigger setting)
    // covers activation there.
    //
    // Runtime gate (inside mouseenter): a desktop browser in
    // mobile-preview mode still satisfies the pointer query
    // (the underlying device has a mouse), so the customizer's
    // mobile preview would still hover-activate without this.
    // Re-checking viewport width on each mouseenter ensures the
    // behaviour matches what real mobile users experience --
    // tap/click only at <992px, hover only at ≥992px.
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    this.buttons.forEach((btn) => {
      btn.addEventListener(
        'mouseenter',
        () => {
          if (!window.matchMedia('(min-width: 992px)').matches) return;
          this._cancelHoverTimer();
          this._hoverTimer = window.setTimeout(() => {
            this._activateWithFade(btn);
          }, HOVER_DWELL_MS);
        },
        { signal: this.abortController.signal }
      );
      btn.addEventListener(
        'mouseleave',
        () => this._cancelHoverTimer(),
        { signal: this.abortController.signal }
      );
    });
  }

  _cancelHoverTimer() {
    if (this._hoverTimer != null) {
      window.clearTimeout(this._hoverTimer);
      this._hoverTimer = null;
    }
  }

  /**
   * Standard WAI-ARIA tablist keyboard pattern: Left/Right move
   * focus between tabs; Home / End jump to first / last. Tab
   * activation follows focus.
   */
  _bindKeyboard() {
    this.headerEl.addEventListener(
      'keydown',
      (event) => {
        const key = event.key;
        if (key !== 'ArrowRight' && key !== 'ArrowLeft' && key !== 'Home' && key !== 'End') {
          return;
        }
        event.preventDefault();
        const activeIdx = this.buttons.findIndex((b) => b === document.activeElement);
        let nextIdx = activeIdx;
        if (key === 'ArrowRight') nextIdx = (activeIdx + 1) % this.buttons.length;
        else if (key === 'ArrowLeft')
          nextIdx = (activeIdx - 1 + this.buttons.length) % this.buttons.length;
        else if (key === 'Home') nextIdx = 0;
        else if (key === 'End') nextIdx = this.buttons.length - 1;

        const nextBtn = this.buttons[nextIdx];
        if (nextBtn) {
          this._activateWithFade(nextBtn);
          nextBtn.focus();
        }
      },
      { signal: this.abortController.signal }
    );
  }

  _bindCustomizerSelect() {
    document.addEventListener(
      'shopify:block:select',
      (event) => {
        if (!this.root.contains(event.target)) return;
        const btn = event.target.closest('.content-tabs__tab-btn');
        if (btn && this.buttons.includes(btn)) this._activateWithFade(btn);
      },
      { signal: this.abortController.signal }
    );
  }

  /**
   * Activate without fade (initial paint). Snaps the active
   * button + panel into state without transition orchestration.
   */
  _activateInstant(btn) {
    const targetKey = btn.getAttribute('data-tab');
    this._setButtonsActive(btn);
    this.panels.forEach((p) => {
      const isActive = p.getAttribute('data-tab') === targetKey;
      p.classList.toggle('is-hidden', !isActive);
    });
    this._activeBtn = btn;
  }

  /**
   * Activate with fade — snap-clean + fade-in.
   *
   * Earlier versions ran a two-leg fade (old panel fades out
   * over the transition duration, then display swap, then new
   * panel fades in). That sequence had two problems:
   *   - Slow: dwell + fade-out + fade-in ≈ 680ms before the
   *     user saw new content.
   *   - Fragile: a hover-interrupt during the fade-out timer
   *     left the old panel stuck in `.is-leaving` (opacity 0 +
   *     display block, occupying layout space) -- the merchant
   *     reported leftover panels staying around after rapid
   *     tab switches.
   *
   * This simpler approach is robust to any rapid-switch
   * sequence: every non-target panel is snap-hidden (with all
   * stale fade classes cleared) and the target panel fades in
   * from opacity 0. Total latency is now ~half (just the
   * fade-in leg), the state machine always converges to
   * "target visible, rest hidden", and there's no in-flight
   * timer to corrupt on interrupt.
   */
  _activateWithFade(btn) {
    if (btn === this._activeBtn) return;

    const targetKey = btn.getAttribute('data-tab');
    const newPanel = this.panels.find((p) => p.getAttribute('data-tab') === targetKey);

    // Button state flips immediately so the indicator chrome
    // reflects the user's intent without waiting for the fade.
    this._setButtonsActive(btn);
    this._activeBtn = btn;

    if (!newPanel) return;

    // Clear any pending timer from a previous activation
    // (defensive -- no timer is currently used in this flow,
    // but `destroy()` and future hooks still touch it).
    if (this._fadeTimer != null) {
      window.clearTimeout(this._fadeTimer);
      this._fadeTimer = null;
    }

    // Snap-clean every non-target panel: clear any stale fade
    // class and set is-hidden. Robust against partial-state
    // leftovers from interrupted previous activations.
    this.panels.forEach((p) => {
      if (p === newPanel) return;
      p.classList.remove('is-leaving', 'is-entering');
      p.classList.add('is-hidden');
    });

    // Show the target panel at opacity 0 (via .is-entering),
    // force a reflow so the browser paints at opacity 0, then
    // remove the class so the CSS transition animates 0 → 1.
    newPanel.classList.remove('is-hidden', 'is-leaving');
    newPanel.classList.add('is-entering');
    void newPanel.offsetHeight;
    newPanel.classList.remove('is-entering');
  }

  _setButtonsActive(activeBtn) {
    this.buttons.forEach((b) => {
      const isActive = b === activeBtn;
      b.classList.toggle('is-active', isActive);
      b.setAttribute('aria-selected', String(isActive));
      b.setAttribute('tabindex', isActive ? '0' : '-1');
    });
  }

  _readTransitionMs() {
    const styles = window.getComputedStyle(this.root);
    const raw = styles.getPropertyValue('--global-transition-duration').trim();
    if (!raw) return 300;
    if (raw.endsWith('ms')) return parseFloat(raw);
    if (raw.endsWith('s')) return parseFloat(raw) * 1000;
    return parseFloat(raw) || 300;
  }

  destroy() {
    this._cancelHoverTimer();
    if (this._fadeTimer != null) {
      window.clearTimeout(this._fadeTimer);
      this._fadeTimer = null;
    }
    this.abortController.abort();
  }
}

function initAll(root) {
  const scope = root || document;
  scope.querySelectorAll(SELECTOR).forEach((el) => {
    if (el[INSTANCE_KEY]) return;
    el[INSTANCE_KEY] = new ContentTabs(el);
  });
}

function destroyAll(root) {
  const scope = root || document;
  scope.querySelectorAll(SELECTOR).forEach((el) => {
    if (el[INSTANCE_KEY]) {
      el[INSTANCE_KEY].destroy();
      el[INSTANCE_KEY] = null;
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll());
} else {
  initAll();
}

document.addEventListener('shopify:section:load', (e) => initAll(e.target));
document.addEventListener('shopify:section:unload', (e) => destroyAll(e.target));
